import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withMethods } from '@ngrx/signals';
import { addEntity, removeEntity, setAllEntities, updateEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';

import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';
import { Module } from '../../models/module.model';
import { ModuleService } from '../parcours-module.service';
import { CreateRequest } from '../requests/create-request';
import { ModuleUpdateRequest } from '../requests/update-request';

export function avecMéthodesModules() {
  return signalStoreFeature(
    withMethods((store) => ({
      ajouterModule: (module: Module) => patchState(store, addEntity(module)),
      chargerModules: (modules: Module[]) => patchState(store, setAllEntities(modules)),
      modifierModule: (state: { id: string; module: Partial<Module> }) =>
        patchState(store, updateEntity({ id: state.id, changes: state.module })),
      selectionnerModule: (state: { id: string }) => patchState(store, { selectedEntityId: state.id }),
      supprimerModule: (state: { id: string }) => patchState(store, removeEntity(state.id)),
    })),
    withMethods((store, service = inject(ModuleService)) => ({
      charger: rxMethod<string>(
        pipe(
          switchMap((idParcours) =>
            service.trouverTous(idParcours).pipe(
              tapResponse({
                next: (result) => store.chargerModules(result.items),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération des modules, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      selection: rxMethod<string>(
        pipe(
          switchMap((idModule) => service.trouveParId(idModule)),
          tapResponse({
            next: (module) => {
              store.modifierModule({ id: module.id, module });
              store.selectionnerModule({ id: module.id });
            },
            error: (err) => {
              console.error(err);
              const toastrId = uuidv7();
              toast.custom(ToastrErrorComponent, {
                componentProps: {
                  message: `Un problème est survenu lors de la récupération du module, contactez le support.`,
                  id: toastrId,
                },
                id: toastrId,
              });
            },
          }),
        ),
      ),
      ajouterUnModule: rxMethod<CreateRequest>(
        pipe(
          switchMap((data) =>
            service.créer(data).pipe(
              tapResponse({
                next: () =>
                  store.ajouterModule({
                    ...data,
                    aptitudes: [],
                    interrelations: [],
                    productions: [],
                    sequences: [],
                    livrablesCaracterises: [],
                    caracteristiques: [],
                  }),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la création d'un module, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        id: string;
        request: ModuleUpdateRequest;
        next?: () => void;
      }>(
        pipe(
          switchMap(({ id, request, next }) =>
            service.modifier(id, request).pipe(
              tapResponse({
                next: () => next?.(),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la modification du module, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<string>(
        pipe(
          switchMap((id) =>
            service.supprimer(id).pipe(
              tapResponse({
                next: () => store.supprimerModule({ id: id }),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la suppression du module, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      dupliquer: rxMethod<{ idModule: string; idÉtape: string; next: () => void }>(
        pipe(
          switchMap(({ idModule, idÉtape, next }) =>
            service.dupliquer(idModule, idÉtape).pipe(
              tapResponse({
                next: (module) => {
                  store.ajouterModule(module);
                  next();
                },
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération du module, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      enregistrerOrdre: rxMethod<{
        id: string;
        request: ModuleUpdateRequest;
      }>(
        pipe(
          switchMap(({ id, request }) =>
            service.modifier(id, request).pipe(
              tapResponse({
                next: () => {
                  store.modifierModule({
                    id: id,
                    module: {
                      nom: request.nom,
                      indice: request.indice,
                    },
                  });
                },
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de l'enregistrement de l'ordre des modules, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
